import React, { useContext, useRef, useState } from 'react'

import { Link, LinkProps } from 'react-router-dom'

import {
  HealthBlob,
  OrgUnitContext,
  ReportProviderContext,
  TimeseriesContext,
  getInsightCategoryLabel,
  getInsightVerticalLabel,
  insightImportanceLabelMap
} from '@percept/app-components'

import {
  Box,
  Card,
  CardStrip,
  Chip,
  Divider,
  Health,
  List,
  ListItem,
  Loader,
  makeAppStyles,
  PercentageBlob,
  Popover,
  ProviderLogo,
  Typography,
  useAppTheme,
} from '@percept/mui'

import { OpenInNew } from '@percept/mui/icons'

import { DashboardCard } from 'components/DashboardCard'

import { AggregatedInsightSourceData, AggregatedInsightUnit, useAggregatedInsightUnits } from './hook'

import { get, intersection, sortBy } from 'lodash-es'

import qs from 'query-string'

import { providerLabelMap, reportProvidersV2 } from '@percept/constants'

import { ReportProviderV2 } from '@percept/types'
import { isPlatformUnitContainer } from '@percept/utils'


const getLatestSourceInsights = (sourceInsights: AggregatedInsightSourceData[]): AggregatedInsightSourceData[] => {
  const byOrg = sourceInsights.reduce( (acc, s) => {
    acc[s.org_unit_id] = acc[s.org_unit_id] || []
    acc[s.org_unit_id].push(s)
    return acc
  }, {} as Record<string, AggregatedInsightSourceData[]>)
  return sortBy(
    Object.values(byOrg).map( data => (
      sortBy(data, 'end_date').reverse()[0]
    )),
    'health'
  )
}


const useStyles = makeAppStyles( theme => ({
  card: {
    display: 'block',
    marginBottom: theme.spacing(3),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1.5),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    fontSize: 14,
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  chipWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1.5),
  },
  chip: {
    marginRight: theme.spacing(1),
    height: 20,
    fontSize: 10,
    padding: 2,
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  categoryChip: {
    color: theme.palette.text.secondary,
  },
  verticalChip: {
    color: (
      theme.palette.type === 'dark' ?
        theme.palette.secondary.light :
        theme.palette.secondary.main
    ),
    borderColor: (
      theme.palette.type === 'dark' ?
        theme.palette.secondary.light :
        theme.palette.secondary.main
    ),
  },
  providerLogo: {
    position: 'relative',
    top: 3,
  },
  popoverTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,

  },
  popoverHealthBlob: {
    display: 'flex',
    marginRight: theme.spacing(1),
  },
  popoverList: {
    // maxHeight: '20rem',
    // overflowY: 'auto',
  },
  popoverDivider: {
    margin: theme.spacing(2, 0, 1, 0),
  },
  popoverLink: {
    display: 'flex',
    alignItems: 'center',
  },
  popoverIcon: {
    marginRight: theme.spacing(2),
  },
  popoverHealth: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
  },
}))


export const AggregatedInsightUnits = () => {
  const orgUnit = useContext(OrgUnitContext)
  const provider = useContext(ReportProviderContext)

  const refs = useRef<Record<string, HTMLDivElement | null>>({})

  const [activeInsight, setActiveInsight] = useState<AggregatedInsightUnit | null>(null)

  const dateRange = useContext(TimeseriesContext)

  const aggregatedInsightQuery = useAggregatedInsightUnits({
    org_unit_id: orgUnit.id,
    start: dateRange && dateRange.start,
    end: dateRange && dateRange.end,
  })

  const classes = useStyles()
  const appTheme = useAppTheme()

  const isParentOrg = isPlatformUnitContainer(orgUnit)

  let matchingInsights: AggregatedInsightUnit[] | null = null
  if( provider ){
    matchingInsights = get(aggregatedInsightQuery.data, provider, null)
  }else if( aggregatedInsightQuery.data ){
    matchingInsights = intersection(
      reportProvidersV2,
      Object.keys(aggregatedInsightQuery.data) as ReportProviderV2[],
    ).map( provider => (aggregatedInsightQuery.data[provider] as AggregatedInsightUnit[])[0] )
  }

  // Hide insight provider when we are in the context of a single provider
  const showInsightProvider = !provider

  const tooltipContentPrefix = provider ? `${providerLabelMap[provider]} insights` : 'Insights'

  return (
    <>
    <DashboardCard
      title='Key Insights'
      tooltipContent={
        isParentOrg ?
          `${tooltipContentPrefix} that are frequently low-scoring across markets / advertisers` :
          `${tooltipContentPrefix} that are frequently low-scoring`
      }
      isRefetching={aggregatedInsightQuery.isRefetching}>
      {
        ( !aggregatedInsightQuery.data && aggregatedInsightQuery.error ) ? (
          <Box
            p={6}
            fontSize={14}
            color='text.secondary'
            display='flex'
            justifyContent='center'>
            An error occurred and the data could not be loaded
          </Box>
        ) : aggregatedInsightQuery.isLoading ? (
          <Loader preset='fullsize' minHeight='10rem' />
        ) : (
          matchingInsights && (
            <Box p={3}>
              { matchingInsights.map( insight => {
                const health = insight.average_health
                const healthColor = (
                  health !== null ?
                    appTheme.chart.healthColourScale(health) :
                    null
                )
                const contrastColor = healthColor ? appTheme.palette.getContrastText(healthColor) : undefined

                // Configure direct links or popover trigger depending on whether we have multiple insights
                // as source for the aggregation
                const isDirectLink = !isParentOrg && insight.source_insights.length === 1

                let props: LinkProps | undefined = undefined
                let WrappingComponent = React.Fragment
                let cardClassName = undefined
                let cardClickHandler = undefined

                if( isDirectLink ){
                  cardClassName = classes.card
                  const directInsight = insight.source_insights[0]
                  WrappingComponent = Link as unknown as React.ExoticComponent
                  props = {
                    className: classes.card,
                    to: {
                      pathname: `/series/${directInsight.report_series_id}`,
                      search: qs.stringify({
                        report: directInsight.report_id,
                        insight: insight.insight_output_unit_definition_id,
                      })
                    }
                  }
                }else{
                  cardClickHandler = ((): void => setActiveInsight(insight))
                  cardClassName = `${classes.card} ${classes.clickable}`
                }

                return (
                  <WrappingComponent {...props} key={insight.insight_output_unit_definition_id}>
                    <Card
                      elevation={2}
                      className={cardClassName}
                      onClick={cardClickHandler}
                      ref={(el): void => {
                        refs.current[insight.insight_output_unit_definition_id] = el as HTMLDivElement
                      }}>
                      <CardStrip color='health' health={insight.average_health} />
                      <div className={classes.cardContent}>
                        <div className={classes.titleContent}>
                          { healthColor && health !== null && (
                            <PercentageBlob
                              value={health * 100}
                              size={30}
                              fontSize={10}
                              color={healthColor}
                              textColor={contrastColor} />
                          )}
                          <Typography variant='h6' className={classes.title}>
                            { insight.title }
                          </Typography>
                        </div>
                        <div className={classes.chipWrapper}>
                          { showInsightProvider && (
                            <Chip
                              classes={{root: classes.chip}}
                              // variant='outlined'
                              // color='primary'
                              size='small'
                              label={
                                providerLabelMap[insight.source_provider]
                              // <ProviderLogo
                              //   provider={insight.source_provider}
                              //   size={1.5}
                              //   className={classes.providerLogo} />
                              } />
                          )}
                          { insight.vertical && (
                            <Chip
                              classes={{root: `${classes.chip} ${classes.verticalChip}`}}
                              variant='outlined'
                              color='secondary'
                              size='small'
                              label={getInsightVerticalLabel(insight.vertical)} />
                          )}
                          { insight.category && (
                            <Chip
                              classes={{root: `${classes.chip} ${classes.categoryChip}`}}
                              variant='outlined'
                              color='default'
                              size='small'
                              label={getInsightCategoryLabel(insight.category)} />
                          )}
                        </div>
                      </div>
                    </Card>
                  </WrappingComponent>
                )
              }) }
            </Box>
          )
        )}
    </DashboardCard>

    { activeInsight && (
      <Popover
        anchorEl={refs.current[activeInsight.insight_output_unit_definition_id]}
        open
        onClose={(): void => setActiveInsight(null)}>
        <CardStrip color='health' health={activeInsight.average_health} />
        <Box px={2} mt={2}>
          <Typography
            className={classes.popoverTitle}
            variant='h5'>
            <div className={classes.popoverHealthBlob}>
              <PercentageBlob
                value={Number(activeInsight.average_health) * 100}
                size={30}
                fontSize={10}
                color={appTheme.chart.healthColourScale(activeInsight.average_health as number)}
                textColor={appTheme.palette.getContrastText(
                  appTheme.chart.healthColourScale(activeInsight.average_health as number)
                )} />
            </div>
            {activeInsight.title}
          </Typography>
          <Divider className={classes.popoverDivider} />
          <List className={classes.popoverList}>
            { getLatestSourceInsights(activeInsight.source_insights).map( insight => (
              <ListItem
                key={insight.insight_output_unit_result_id}
                component={Link}
                className={classes.popoverLink}
                button
                to={{
                  pathname: `/series/${insight.report_series_id}`,
                  search: qs.stringify({
                    report: insight.report_id,
                    insight: activeInsight.insight_output_unit_definition_id,
                  })
                }}>
                <OpenInNew fontSize='small' className={classes.popoverIcon} />
                { insight.org_unit_name }
                <Health
                  value={insight.health}
                  // color='inherit'
                  className={classes.popoverHealth} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    )}

    </>
  )
}
